import { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Grid, Stack, Chip, alpha, Skeleton,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell, ComposedChart, Sector,
} from 'recharts';
import DashboardSection from './DashboardSection';
import DashboardChartFrame from './DashboardChartFrame';
import { useDashboardChartLayout } from './useDashboardChartLayout';
import { useTenantCurrency } from '../../hooks/useTenantCurrency';
import { formatPercent, buildServiceStatusSegments, getServiceAccountedTotal, formatServiceBreakdownHint } from '../../utils/jobStats';
import { DASHBOARD_SERVICE_NAV } from '../../config/dashboardNavigation';

interface ServiceMixItem {
  key: string;
  label: string;
  color: string;
  count: number;
  revenue: number;
  completed: number;
  cancelled: number;
  active: number;
  pending: number;
  other?: number;
  completionRate: number;
  cancelRate: number;
}

interface TrendPoint {
  date: string;
  total: number;
  transport: number;
  food: number;
  grocery: number;
  porter: number;
  on_demand: number;
}

interface PulsePoint {
  hour: string;
  total: number;
  transport: number;
  food: number;
  grocery: number;
  porter: number;
  on_demand: number;
}

interface GodViewData {
  serviceMix?: ServiceMixItem[];
  revenueTrend?: TrendPoint[];
  hourlyPulse?: PulsePoint[];
  userGrowth?: Array<{ date: string; users: number }>;
  platform?: {
    totalTenants?: number;
    activeTenants?: number;
    suspendedTenants?: number;
    deactivatedTenants?: number;
  } | null;
}

interface Props {
  data?: GodViewData;
  loading?: boolean;
  revenueMixFrom?: string | null;
  revenueMixTo?: string | null;
  onRevenueMixDateChange?: (from: string | null, to: string | null) => void;
}

const REVENUE_SERIES = [
  { key: 'transport', label: 'Transport', color: '#2563eb' },
  { key: 'porter', label: 'Porter', color: '#0f766e' },
  { key: 'on_demand', label: 'Services', color: '#0891b2' },
] as const;

function ServiceStatusMixBar({ segments }: { segments: ReturnType<typeof buildServiceStatusSegments> }) {
  const visible = segments.filter((seg) => seg.count > 0);
  if (!visible.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        No status data yet
      </Typography>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          height: 8,
          borderRadius: 999,
          overflow: 'hidden',
          bgcolor: alpha('#64748b', 0.12),
        }}
      >
        {visible.map((seg) => (
          <Box
            key={seg.key}
            sx={{ width: `${Math.max(seg.rate, 0)}%`, bgcolor: seg.color, minWidth: seg.rate > 0 ? 4 : 0 }}
            title={`${seg.label}: ${formatPercent(seg.rate)}`}
          />
        ))}
      </Box>
      <Stack direction="row" flexWrap="wrap" gap={1} mt={1} useFlexGap>
        {visible.map((seg) => (
          <Typography key={seg.key} variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            <Box component="span" sx={{ color: seg.color, fontWeight: 800 }}>{formatPercent(seg.rate)}</Box>
            {' '}{seg.label.toLowerCase()} ({seg.count.toLocaleString()})
          </Typography>
        ))}
      </Stack>
    </>
  );
}

function ChartEmpty({ message }: { message: string }) {
  return (
    <Box display="flex" alignItems="center" justifyContent="center" height="100%" minHeight={180}>
      <Typography variant="body2" color="text.secondary" textAlign="center" px={2}>
        {message}
      </Typography>
    </Box>
  );
}

function VolumeCompletedTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const volume = payload.find((p: any) => p.dataKey === 'volume')?.value ?? 0;
  const completed = payload.find((p: any) => p.dataKey === 'completed')?.value ?? 0;
  return (
    <Paper sx={{ p: 1.25, fontSize: 12, lineHeight: 1.6, boxShadow: 3 }}>
      <Typography variant="subtitle2" fontWeight={700}>{label}</Typography>
      <Typography variant="body2" color="text.secondary">Volume: <b>{volume}</b></Typography>
      <Typography variant="body2" color="text.secondary">Completed: <b>{completed}</b></Typography>
    </Paper>
  );
}

export default function DashboardGodViewAnalytics({ data, loading, revenueMixFrom, revenueMixTo, onRevenueMixDateChange }: Props) {
  const layout = useDashboardChartLayout();
  const navigate = useNavigate();
  const { formatAmount, formatCompact } = useTenantCurrency();
  const serviceMix = useMemo(
    () => (data?.serviceMix || []).filter((s) => s.key !== 'food' && s.key !== 'grocery'),
    [data?.serviceMix],
  );
  const [activeRevenueMixLabel, setActiveRevenueMixLabel] = useState<string | null>(null);

  // Revenue trend (30-day) uses daily points; we intentionally reduce tick density
  // and reserve more bottom margin so X-axis labels are always readable.
  const revenueTrendLen = data?.revenueTrend?.length ?? 0;
  const revenueChartMargin = {
    ...layout.margin,
    bottom: layout.isMobile ? 80 : 60,
  };
  const revenueTickInterval = revenueTrendLen > 1
    ? Math.max(1, Math.floor((revenueTrendLen - 1) / 8))
    : 0;
  const revenueAxisAngle = revenueTrendLen > 12 ? (layout.isMobile ? -35 : -25) : 0;
  const revenueTextAnchor = revenueAxisAngle !== 0 ? ('end' as const) : (layout.axis.x.textAnchor as 'end' | 'middle');
  const revenueTickHeight = revenueAxisAngle !== 0 ? (layout.isMobile ? 58 : 46) : layout.axis.x.height;
  const formatRevenueDateTick = (value: string) => {
    if (!value) return value;
    // Backend uses day grouping; normalize ISO strings to YYYY-MM-DD, then show MM/DD.
    const isoDay = value.includes('T') ? value.slice(0, 10) : value;
    if (isoDay.length >= 10 && isoDay[4] === '-' && isoDay[7] === '-') {
      const [_, mm, dd] = isoDay.split('-');
      if (mm && dd) return `${mm}/${dd}`;
    }
    // Fallback: reuse shared formatter (shortens on mobile).
    return layout.formatDateTick(value);
  };
  const pieData = useMemo(
    () => serviceMix.filter((s) => s.revenue > 0 || s.count > 0).map((s) => ({
      name: s.label,
      value: s.revenue || s.count,
      color: s.color,
    })),
    [serviceMix],
  );

  const pieTotal = useMemo(
    () => pieData.reduce((sum, p) => sum + (Number(p.value) || 0), 0),
    [pieData],
  );

  const activeRevenueMixIndex = useMemo(() => {
    if (!activeRevenueMixLabel) return -1;
    return pieData.findIndex((p) => p.name === activeRevenueMixLabel);
  }, [activeRevenueMixLabel, pieData]);

  const renderActiveRevenueMix = (props: any) => {
    const {
      cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill,
    } = props;

    return (
      <g>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius + 10}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
          stroke={fill}
          strokeWidth={2}
        />
      </g>
    );
  };

  const barData = useMemo(
    () => serviceMix.map((s) => ({
      key: s.key,
      name: s.label,
      volume: s.count,
      revenue: s.revenue,
      completed: s.completed,
      cancelled: s.cancelled,
      color: s.color,
    })),
    [serviceMix],
  );

  return (
    <Box mb={3} sx={{ width: '100%', minWidth: 0 }}>
      <DashboardSection
        title="Analytics command center"
        subtitle="Transport, porter, and services revenue, volume, and performance"
        accent="#6366f1"
      >
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap mb={2}>
          {serviceMix.map((s) => (
            <Chip key={s.key} size="small" label={`${s.label}: ${s.count}`} sx={{ bgcolor: alpha(s.color, 0.1), color: s.color, fontWeight: 700 }} />
          ))}
        </Stack>

        {data?.platform && (
          <Grid container spacing={2} mb={3}>
            {[
              { label: 'Total tenants', value: data.platform.totalTenants, color: '#2563eb' },
              { label: 'Active', value: data.platform.activeTenants, color: '#16a34a' },
              { label: 'Suspended', value: data.platform.suspendedTenants, color: '#f59e0b' },
              { label: 'Deactivated', value: data.platform.deactivatedTenants, color: '#64748b' },
            ].map((p) => (
              <Grid item xs={6} sm={6} md={3} key={p.label}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: 1, borderColor: alpha(p.color, 0.2), bgcolor: alpha(p.color, 0.05), height: '100%' }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>{p.label}</Typography>
                  <Typography variant="h4" fontWeight={900} sx={{ color: p.color, fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>{p.value ?? 0}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}

        <Grid container spacing={2}>
          {serviceMix.map((s) => {
            const total = getServiceAccountedTotal(s);
            const statusSegments = buildServiceStatusSegments(s, {
              completed: 'Completed',
              active: 'In progress',
            });

            return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={s.key}>
              <Paper
                elevation={0}
                component={DASHBOARD_SERVICE_NAV[s.key] ? RouterLink : 'div'}
                to={DASHBOARD_SERVICE_NAV[s.key]}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: 1,
                  borderColor: alpha(s.color, 0.2),
                  background: `linear-gradient(160deg, ${alpha(s.color, 0.1)}, transparent)`,
                  height: '100%',
                  textDecoration: 'none',
                  color: 'inherit',
                  display: 'block',
                  cursor: DASHBOARD_SERVICE_NAV[s.key] ? 'pointer' : 'default',
                  '&:hover': DASHBOARD_SERVICE_NAV[s.key] ? {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${alpha(s.color, 0.16)}`,
                  } : undefined,
                }}
              >
                {loading ? <Skeleton height={100} /> : (
                  <>
                    <Typography variant="caption" fontWeight={700} color="text.secondary">{s.label}</Typography>
                    <Typography variant="h5" fontWeight={900} sx={{ color: s.color }}>{total.toLocaleString()}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" mt={0.5} sx={{ wordBreak: 'break-word' }}>
                      {formatAmount(s.revenue)} · {formatServiceBreakdownHint({
                        total,
                        completed: s.completed,
                        pending: s.pending,
                        active: s.active,
                        cancelled: s.cancelled,
                        other: s.other,
                        completedLabel: 'completed',
                        activeLabel: 'in progress',
                      })}
                    </Typography>
                    <Box mt={1.5}>
                      <Stack direction="row" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption">Status mix</Typography>
                        <Typography variant="caption" fontWeight={800}>
                          {formatPercent(statusSegments.reduce((sum, seg) => sum + seg.rate, 0))} total
                        </Typography>
                      </Stack>
                      <ServiceStatusMixBar segments={statusSegments} />
                    </Box>
                  </>
                )}
              </Paper>
            </Grid>
            );
          })}
        </Grid>
      </DashboardSection>

      <DashboardSection title="Revenue trend (30 days)" subtitle="Stacked revenue — transport, porter, and services" accent="#2563eb">
        <Stack direction="row" spacing={1} flexWrap="wrap" mb={1.25} useFlexGap>
          {REVENUE_SERIES.map((s) => (
            <Stack key={s.key} direction="row" alignItems="center" spacing={0.75}>
              <Box sx={{ width: 10, height: 10, borderRadius: 999, bgcolor: s.color }} />
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                {s.label}
              </Typography>
            </Stack>
          ))}
        </Stack>
        <DashboardChartFrame loading={loading} size="lg">
          {(data?.revenueTrend?.length ?? 0) > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data?.revenueTrend} margin={revenueChartMargin}>
                <defs>
                  {REVENUE_SERIES.map((s) => (
                    <linearGradient key={s.key} id={`grad-${s.key}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={s.color} stopOpacity={0.35} />
                      <stop offset="95%" stopColor={s.color} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={alpha('#64748b', 0.15)} />
                <XAxis
                  dataKey="date"
                  tick={layout.axis.tick}
                  angle={revenueAxisAngle}
                  textAnchor={revenueTextAnchor}
                  height={revenueTickHeight}
                  interval={revenueTickInterval}
                  tickMargin={10}
                  tickFormatter={formatRevenueDateTick}
                />
                <YAxis tick={layout.axis.tick} width={layout.axis.y.width} tickFormatter={(v) => formatCompact(v)} />
                <Tooltip {...layout.tooltip} formatter={(v: number) => [formatAmount(v), '']} />
                {REVENUE_SERIES.map((s) => (
                  <Area
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    stackId="1"
                    stroke={s.color}
                    fill={`url(#grad-${s.key})`}
                    name={s.label}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <ChartEmpty message="No revenue data yet" />
          )}
        </DashboardChartFrame>
      </DashboardSection>

      <DashboardSection title="Revenue mix" subtitle="Share by service" accent="#7c3aed">
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack direction="row" spacing={1.5} mb={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
            <DatePicker
              label="From"
              value={revenueMixFrom ? dayjs(revenueMixFrom) : null}
              onChange={(v: Dayjs | null) => onRevenueMixDateChange?.(v ? v.startOf('day').toISOString() : null, revenueMixTo ?? null)}
              slotProps={{ textField: { size: 'small', sx: { maxWidth: 160 } } }}
              maxDate={revenueMixTo ? dayjs(revenueMixTo) : undefined}
            />
            <DatePicker
              label="To"
              value={revenueMixTo ? dayjs(revenueMixTo) : null}
              onChange={(v: Dayjs | null) => onRevenueMixDateChange?.(revenueMixFrom ?? null, v ? v.endOf('day').toISOString() : null)}
              slotProps={{ textField: { size: 'small', sx: { maxWidth: 160 } } }}
              minDate={revenueMixFrom ? dayjs(revenueMixFrom) : undefined}
            />
          </Stack>
        </LocalizationProvider>
        <Stack direction="row" spacing={1} flexWrap="wrap" mb={1.25} useFlexGap>
          {pieData.map((entry) => {
            const isActive = activeRevenueMixLabel === entry.name;
            const sharePct = pieTotal > 0 ? (entry.value / pieTotal) * 100 : 0;
            return (
              <Box
                key={entry.name}
                role="button"
                tabIndex={0}
                onClick={() => setActiveRevenueMixLabel(entry.name)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') setActiveRevenueMixLabel(entry.name);
                }}
                sx={{
                  cursor: 'pointer',
                  p: 0.6,
                  borderRadius: 2,
                  border: `1px solid ${isActive ? alpha(entry.color, 0.55) : alpha('#64748b', 0.25)}`,
                  bgcolor: isActive ? alpha(entry.color, 0.1) : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  userSelect: 'none',
                  outline: 'none',
                }}
              >
                <Box sx={{ width: 10, height: 10, borderRadius: 999, bgcolor: entry.color }} />
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  {entry.name} · {formatPercent(sharePct)}
                </Typography>
              </Box>
            );
          })}
        </Stack>
        <DashboardChartFrame loading={loading} size="md">
          {pieData.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart margin={{ top: 8, bottom: layout.isMobile ? 48 : 32 }}>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={layout.pie.innerRadius}
                  outerRadius={layout.pie.outerRadius}
                  paddingAngle={3}
                  cx="50%"
                  cy={layout.pie.cy}
                  activeIndex={activeRevenueMixIndex}
                  activeShape={renderActiveRevenueMix}
                >
                  {pieData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  {...layout.tooltip}
                  content={({ active, payload }) => {
                    if (!active || !payload || payload.length === 0) return null;
                    const slice = (payload[0] as any)?.payload || {};
                    const name = slice.name ?? (payload[0] as any)?.name ?? 'Service';
                    const value = Number(slice.value ?? (payload[0] as any)?.value ?? 0);
                    const pct = pieTotal > 0 ? (value / pieTotal) * 100 : 0;

                    return (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 1.25,
                          borderRadius: 1.5,
                          border: '1px solid #e2e8f0',
                          bgcolor: 'background.paper',
                          maxWidth: 220,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" fontWeight={800}>
                          {name}
                        </Typography>
                        <Typography variant="body2" fontWeight={900} sx={{ mt: 0.25 }}>
                          {formatAmount(value)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                          {formatPercent(pct)} of total
                        </Typography>
                      </Paper>
                    );
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <ChartEmpty message="No mix data" />
          )}
        </DashboardChartFrame>
      </DashboardSection>

      <DashboardSection title="4-hour live pulse" subtitle="Hourly job volume (last 4 hours) — transport, delivery, and services" accent="#0891b2">
        {/* Legend */}
        <Stack direction="row" spacing={1.5} flexWrap="wrap" mb={1} useFlexGap>
          {[...REVENUE_SERIES, { key: 'total', label: 'Total', color: '#0f172a' }].map((s) => (
            <Stack key={s.key} direction="row" spacing={0.5} alignItems="center">
              <Box sx={{ width: 10, height: 10, borderRadius: s.key === 'total' ? 0 : 999, bgcolor: s.color }} />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>{s.label}</Typography>
            </Stack>
          ))}
        </Stack>
        <DashboardChartFrame loading={loading} size="lg">
          {(data?.hourlyPulse?.length ?? 0) > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={data?.hourlyPulse} margin={{ ...layout.margin, bottom: 30 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={alpha('#64748b', 0.15)} />
                <XAxis
                  dataKey="hour"
                  tick={{ fontSize: 12, fill: '#475569' }}
                  interval={0}
                  height={40}
                  label={{ value: 'Hour', position: 'insideBottom', offset: -5, fontSize: 11, fill: '#94a3b8' }}
                />
                <YAxis tick={layout.axis.tick} width={layout.axis.y.width} allowDecimals={false} />
                <Tooltip {...layout.tooltip} />
                {REVENUE_SERIES.map((s, i) => (
                  <Bar
                    key={s.key}
                    dataKey={s.key}
                    stackId="a"
                    fill={s.color}
                    name={s.label}
                    radius={i === REVENUE_SERIES.length - 1 ? [4, 4, 0, 0] : undefined}
                  />
                ))}
                <Line type="monotone" dataKey="total" stroke="#0f172a" strokeWidth={2} dot={false} name="Total" />
              </ComposedChart>
            </ResponsiveContainer>
          ) : (
            <ChartEmpty message="No activity in the last 4 hours" />
          )}
        </DashboardChartFrame>
      </DashboardSection>

      <DashboardSection title="Service volume vs completed" subtitle="Volume and completion by service · Click a bar to view orders" accent="#6366f1">
        <DashboardChartFrame loading={loading} size="md">
          {barData.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={barData}
                margin={layout.margin}
                onClick={(state) => {
                  const key = (state?.activePayload?.[0]?.payload as any)?.key;
                  const path = key && DASHBOARD_SERVICE_NAV[key];
                  if (path) navigate(path);
                }}
                style={{ cursor: 'pointer' }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={alpha('#64748b', 0.15)} />
                <XAxis
                  dataKey="name"
                  tick={layout.axis.tick}
                  angle={layout.axis.x.angle}
                  textAnchor={layout.axis.x.textAnchor}
                  height={layout.axis.x.height}
                  interval={0}
                />
                <YAxis tick={layout.axis.tick} width={layout.axis.y.width} allowDecimals={false} />
                <Tooltip content={<VolumeCompletedTooltip />} />
                <Legend {...layout.legend} />
                <Bar dataKey="volume" fill="#6366f1" name="Volume" radius={[4, 4, 0, 0]} />
                <Bar dataKey="completed" fill="#16a34a" name="Completed" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <ChartEmpty message="No service data" />
          )}
        </DashboardChartFrame>
      </DashboardSection>

      <DashboardSection title="Completed vs cancelled" subtitle="Per-service outcome comparison" accent="#16a34a">
        <DashboardChartFrame loading={loading} size="md">
          {barData.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barData} margin={layout.margin}>
                <CartesianGrid strokeDasharray="3 3" stroke={alpha('#64748b', 0.15)} />
                <XAxis
                  dataKey="name"
                  tick={layout.axis.tick}
                  angle={layout.axis.x.angle}
                  textAnchor={layout.axis.x.textAnchor}
                  height={layout.axis.x.height}
                  interval={0}
                />
                <YAxis tick={layout.axis.tick} width={layout.axis.y.width} />
                <Tooltip {...layout.tooltip} />
                <Legend {...layout.legend} />
                <Bar dataKey="completed" fill="#16a34a" name="Completed" radius={[4, 4, 0, 0]} />
                <Bar dataKey="cancelled" fill="#dc2626" name="Cancelled" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <ChartEmpty message="No outcome data" />
          )}
        </DashboardChartFrame>
      </DashboardSection>

      <DashboardSection title="User growth (30 days)" subtitle="New customer registrations · Click a point to view users registered on that date" accent="#2563eb">
        <DashboardChartFrame loading={loading} size="md">
          {(data?.userGrowth?.length ?? 0) > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data?.userGrowth}
                margin={{ ...layout.margin, bottom: 35 }}
                onClick={(state) => {
                  const date = (state?.activePayload?.[0]?.payload as any)?.date;
                  if (date) navigate(`/users?userType=customer&registeredDate=${date}`);
                }}
                style={{ cursor: 'pointer' }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={alpha('#64748b', 0.15)} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: '#475569' }}
                  angle={-35}
                  textAnchor="end"
                  height={50}
                  interval={Math.max(0, Math.ceil((data?.userGrowth?.length ?? 0) / 10) - 1)}
                  tickFormatter={(v: string) => {
                    const parts = v.split('-');
                    return parts.length >= 3 ? `${parts[1]}/${parts[2]}` : v;
                  }}
                />
                <YAxis tick={layout.axis.tick} width={layout.axis.y.width} allowDecimals={false} />
                <Tooltip
                  content={({ active, payload, label }: any) => {
                    if (!active || !payload?.length) return null;
                    return (
                      <Paper sx={{ p: 1.25, fontSize: 12, boxShadow: 3 }}>
                        <Typography variant="subtitle2" fontWeight={700}>{label}</Typography>
                        <Typography variant="body2" color="text.secondary">New users: <b>{payload[0].value}</b></Typography>
                      </Paper>
                    );
                  }}
                />
                <Line type="monotone" dataKey="users" stroke="#2563eb" strokeWidth={3} dot={{ r: 3 }} activeDot={{ r: 6 }} name="New users" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <ChartEmpty message="No user growth data" />
          )}
        </DashboardChartFrame>
      </DashboardSection>
    </Box>
  );
}
